<template>
  <form-section>
    <rest-list-editable
      ref="rest-list-editable"
      :payload="fetchEndpoint"
      :rules="rules"
      :save-warning="channelsUpdated"
      :add-endpoint="addEndpoint"
      :update-endpoint="updateEndpoint"
      @row-updated="onRowUpdated"
      @row-discarded="onRowDiscard"
      :before-save="beforeSaveRow"
    >
      <rest-list-column
        prop="name"
        :fixed="true"
        :width="300"
        :editable="true"
        :title="$t('SubscriptionPreference_Field_Name')"
      >
        <template slot="input-edit" slot-scope="{ model }">
          <span v-if="model.id">{{ model.name }}</span>
          <el-input
            v-model="model.name"
            v-else
            @keydown.native="handleTabKey"
            data-qa="preference-name-input"
          />
        </template>
      </rest-list-column>

      <rest-list-column
        prop="display_name"
        :editable="true"
        :width="300"
        :title="$t('SubscriptionPreference_Field_DisplayName')"
      >
        <template slot-scope="{ scope }" slot="default">
          <span v-if="scope.row.isDisplayNamesDifferent">
            <el-tag type="warning" size="mini">
              {{
                $t(
                  'ConsentManager_Custom_Channels_DisplayName_UpdatePlaceholder'
                )
              }}
            </el-tag>
            <el-tooltip placement="right">
              <div
                class="ConsentNameInfo--Tooltip"
                slot="content"
                v-html="
                  $t(
                    'ConsentManager_Custom_Channels_DisplayName_UpdatePlaceholder_Description'
                  )
                "
              />
              <span><i class="el-icon-warning-outline"></i></span>
            </el-tooltip>
          </span>
          <span v-else> {{ scope.row.display_name }}</span>
        </template>
        <template slot="input-edit" slot-scope="{ model }">
          <el-input
            value=""
            v-model="displayName"
            v-if="model.isDisplayNamesDifferent"
          />
          <el-input
            v-model="model.display_name"
            class="display_name"
            data-qa="preference-display-name-input"
            v-else
          />
        </template>
      </rest-list-column>

      <rest-list-column-enum
        prop="type"
        :editable="true"
        :enum="consentPreferenceType"
        :title="$t('SubscriptionPreference_Field_Type')"
        :width="200"
        :number="true"
      >
        <template slot="input-edit" slot-scope="{ model }">
          <enum-select
            v-model="model.type"
            :options="consentPreferenceType"
            :number="true"
            :disabled="!!model.id"
            data-qa="preference-type-dropdown"
          />
        </template>
      </rest-list-column-enum>

      <rest-list-column
        prop="channel"
        :min-width="channelsWidth"
        :editable="true"
        :filterable="false"
        :filter-input="false"
        :sortable="false"
        :title="$t('SubscriptionPreference_Field_Channel')"
      >
        <template slot-scope="{ scope }" slot="default">
          <div class="channels-checkboxes-wrapper-disabled">
            <div class="channels-checkboxes">
              <checkbox
                :key="channel.key"
                disabled
                :title="$t(channel.name)"
                :value="scope.row.channelList[channel.key]"
                v-for="channel in channelsList"
              />
            </div>
          </div>
        </template>
        <template slot="input-edit" slot-scope="{ model }">
          <div class="channels-checkboxes-wrapper-editable">
            <div class="channels-checkboxes">
              <checkbox
                :key="channel.key"
                :title="$t(channel.name)"
                v-model="model.channelList[channel.key]"
                @input="checkboxChange"
                v-for="channel in channelsList"
                :data-qa="`channel-${channel.key}-checkbox`"
                @keydown.native="handleChannelTab"
              />
            </div>
          </div>
        </template>
      </rest-list-column>

      <rest-list-column-actions
        v-if="!readonly"
        :editable="true"
        :fixed="true"
        fixed="right"
      >
        <template slot-scope="scope">
          <rest-list-action
            icon="el-icon-view"
            :title="$t('SubscriptionPreference_Custom_ViewLogs')"
            :click="() => viewLogs(scope.row)"
            data-qa="view-logs-action-button"
          />
          <rest-list-action
            icon="el-icon-delete"
            :title="$t('SubscriptionPreference_Custom_Delete')"
            :click="() => confirmDelete(scope.row)"
            data-qa="delete-action-button"
          />
        </template>
      </rest-list-column-actions>
    </rest-list-editable>

    <delete-popup-warning
      :show-warning-dialog="showWarningDialog"
      :show-confirm-dialog="showConfirmDialog"
      entity-type="preference"
      :item-name="currentItem ? currentItem.name : ''"
      :warning-items="warningItems"
      @cancel="cancelDelete"
      @proceed="proceedToConfirm"
      @confirm="handleDeleteConfirm"
    />
  </form-section>
</template>

<script>
import RestListColumn from '@/common/components/rest/RestListColumn'
import { projectEndpoint } from '@/core/helpers'
import { SubscriptionPreferenceChannelLabels } from '@/modules/settings/enum/SubscriptionPreferenceChannelEnum'
import RestListEditable from '@/common/components/rest/RestListEditable'
import RestListColumnActions from '@/common/components/rest/RestListColumnActions'
import { EventBus } from '@/core/EventBus'
import Checkbox from '@/common/components/form/Checkbox'
import RestApi from '@/core/services/RestApi'
import FormSection from '@/common/components/form/FormSection'
import RestListColumnEnum from '@/common/components/rest/RestListColumnEnum'
import { consentPreferenceType } from '@/modules/consent-manager/enum/ConsentPreferenceEnums'
import EnumSelect from '@/common/components/form/EnumSelect'
import deletePopupWarningUtils from '@/modules/consent-manager/utils/deletePopupWarningUtils'
import auditLogFilterUtils from '@/modules/consent-manager/utils/auditLogFilterUtils'
import DeletePopupWarning from '@/modules/consent-manager/components/DeletePopupWarning'
import RestListAction from '@/common/components/rest/RestListAction'

export default {
  name: 'ConsentManagerSubscriptionPreferencesPage',
  mixins: [
    deletePopupWarningUtils.createDeleteMixin({
      componentRefName: 'rest-list-editable'
    }),
    auditLogFilterUtils.createViewLogsMixin({
      channelType: auditLogFilterUtils.CHANNEL_TYPES.PREFERENCE
    })
  ],
  components: {
    FormSection,
    Checkbox,
    RestListColumnActions,
    RestListEditable,
    RestListColumn,
    RestListColumnEnum,
    EnumSelect,
    DeletePopupWarning,
    RestListAction
  },
  mounted() {
    EventBus.$on('add-subscription-preference-row', this.addRow)
    RestApi(this.channelsEndpoint)
      .then(({ data }) => {
        this.consentChannels = data
        setTimeout(() => {
          this.channelsColumnWidth = document
            .querySelector('.channels-checkboxes')
            .getBoundingClientRect().width
        }, 500)
      })
      .catch(error => {
        this.$message({
          message: error.message,
          type: 'error'
        })
        this.loading = false
      })
  },
  destroyed() {
    EventBus.$off('add-subscription-preference-row')
  },
  data() {
    return {
      SubscriptionPreferenceChannelLabels,
      consentPreferenceType,
      consentChannels: [],
      channelsUpdated: false,
      channelsColumnWidth: null,
      tempDisplayName: '',
      showWarningDialog: false,
      showConfirmDialog: false,
      currentPreference: null
    }
  },
  computed: {
    warningItems() {
      return [
        this.$t('SubscriptionPreference_Warning_Remove_Consent_Data'),
        this.$t('SubscriptionPreference_Warning_Break_Campaigns'),
        this.$t('SubscriptionPreference_Warning_Disrupt'),
        this.$t('SubscriptionPreference_Warning_Incorrect_Data'),
        this.$t('SubscriptionPreference_Warning_Break_Integration')
      ]
    },
    displayName: {
      get() {
        return this.tempDisplayName
      },
      set(val) {
        this.tempDisplayName = val
        let row = this.$refs['rest-list-editable'].editableRow
        this.$set(row, 'display_name', val)
      }
    },
    rules() {
      return {
        name: [
          {
            required: true,
            trigger: 'blur',
            message: this.$t('SubscriptionPreference_Field_Name_Required')
          },
          {
            max: 100,
            trigger: 'blur',
            message: this.$t('SubscriptionPreference_Field_Name_TooLong')
          },
          {
            validator: this.validatePreferenceName
          }
        ],
        display_name: {
          max: 100,
          trigger: 'blur',
          message: this.$t('SubscriptionPreference_Field_DisplayName_TooLong')
        },
        type: {
          required: true,
          trigger: 'blur',
          message: this.$t('SubscriptionPreference_Field_Type_Required')
        }
      }
    },

    pageTitle() {
      return this.$t(this.$route.meta.title)
    },

    pageBreadcrumbs() {
      return [
        {
          label: this.$t('Settings_Breadcrumb_Settings')
        },
        {
          label: this.$t('Settings_Breadcrumb_SubscriptionPreferences')
        }
      ]
    },

    fetchEndpoint() {
      return projectEndpoint({
        method: 'get',
        url: `consent-subscription-preferences`,
        params: {
          sort: 'name'
        }
      })
    },

    readonly() {
      return !this.$can('SubscriptionPreference::Update')
    },

    channelsEndpoint() {
      return projectEndpoint({
        method: 'get',
        url: `consent-channels`,
        params: {
          sort: 'name'
        }
      })
    },

    staticChannels() {
      if (!this.SubscriptionPreferenceChannelLabels) return []
      return Object.keys(this.SubscriptionPreferenceChannelLabels).map(key => {
        return { key: key, name: this.SubscriptionPreferenceChannelLabels[key] }
      })
    },

    channelsList() {
      return [...this.staticChannels, ...this.consentChannels]
    },

    channelsWidth() {
      if (this.channelsColumnWidth) return this.channelsColumnWidth + 20
      let lettersCount = 0
      this.channelsList.forEach(c => {
        lettersCount += c.name.length
      })
      return this.channelsList.length * 30 + lettersCount * 7.9
    }
  },

  methods: {
    handleTabKey(e) {
      if (e.keyCode === 9) {
        document.querySelector('.display_name input').focus()
        e.preventDefault()
      }
    },
    handleChannelTab(e) {
      if (e.keyCode !== 9 || e.shiftKey) return

      const channelInputs = Array.from(
        document.querySelectorAll('[data-qa^="channel-"] input')
      )
      if (channelInputs.length === 0) return

      const lastInputInList = channelInputs[channelInputs.length - 1]
      const lastInputQA = lastInputInList
        .closest('[data-qa^="channel-"]')
        .getAttribute('data-qa')
      const currentTargetQA = e.target
        .closest('[data-qa^="channel-"]')
        .getAttribute('data-qa')
      const isLastCheckbox = lastInputQA === currentTargetQA

      if (isLastCheckbox) {
        e.preventDefault()

        const typeInput = document.querySelector(
          '[data-qa="preference-type-dropdown"] input'
        )
        const isEditMode = typeInput && typeInput.disabled

        if (isEditMode) {
          document.querySelector('.display_name input').focus()
        } else {
          document.querySelector('.el-table__fixed .table-header input').focus()
        }
      }
    },
    beforeSaveRow(row) {
      if (row.isDisplayNamesDifferent) {
        row.display_name = ''
      }
    },

    onRowUpdated() {
      this.channelsUpdated = false
    },

    onRowDiscard() {
      this.tempDisplayName = ''
    },

    checkboxChange() {
      this.channelsUpdated = true
    },

    addEndpoint() {
      return projectEndpoint({
        method: 'post',
        url: `consent-subscription-preferences/save-subscription`
      })
    },

    updateEndpoint() {
      return projectEndpoint({
        method: 'post',
        url: `consent-subscription-preferences/save-subscription`
      })
    },

    deleteEndpoint(row) {
      return projectEndpoint({
        method: 'post',
        url: `consent-subscription-preferences/remove-subscription`,
        data: { name: row.name }
      })
    },

    addRow() {
      this.$refs['rest-list-editable'].addRow({
        name: '',
        display_name: '',
        channelList: {}
      })
    },

    validatePreferenceName(rule, value, callback) {
      let row = this.$refs['rest-list-editable'].editableRow
      if (
        row.hasOwnProperty('id') ||
        /^[A-Za-z0-9 _~\-!@#$%^&*()]+$/.test(value)
      ) {
        callback()
        return
      }

      callback(new Error(this.$t('SubscriptionPreferences_Invalid_Name')))
    },
    handleDeleteConfirm() {
      this.executeDelete(this.deleteEndpoint)
    }
  }
}
</script>

<style scoped>
.ConsentNameInfo--Tooltip {
  max-width: 300px;
}

.channels-checkboxes {
  display: inline-flex;
}
.channels-checkboxes-wrapper-disabled {
  display: flex;
}
.channels-checkboxes-wrapper-editable {
  display: block;
  padding-top: 5.5px;
}
>>> .el-checkbox {
  padding: 0 !important;
}
>>> .channels-checkboxes .is-disabled.is-checked .el-checkbox__inner {
  background-color: #a1a3a7 !important;
  border-color: #878282ff !important;
}
>>> .channels-checkboxes .is-disabled .el-checkbox__inner,
>>> .channels-checkboxes .is-disabled .el-checkbox__inner::after,
>>> .channels-checkboxes .is-disabled .el-checkbox__label {
  cursor: default !important;
}

>>> .el-table__fixed-body-wrapper {
  z-index: 51 !important;
}
</style>



// WEBPACK FOOTER //
// src/modules/consent-manager/pages/ConsentManagerSubscriptionPreferencesPage.vue